import { createEffect, createEvent, createStore } from "effector";
// STORE
import {
  serUserStates,
  setUserData,
  setUserError,
  setUserLoading,
  setUserAddPermissions,
} from "@store/user-store";
import {
  setInternalStudentStates,
  setInternalStudent,
  setInternalStudentStateError,
  setInternalStudentStateLoading,
} from "@store/internal-study/students-store";
import { setCompany } from "@store/company/company-store";

import AuthService from "@services/auth-service";

import {
  AuthStoreLoginPropsT,
  AuthStoreSignupPropsT,
} from "@interfaces/store/auth-store-types";
import { AuthStorePropsT } from "@interfaces/requests/auth";
import { companyService } from "@services/company-service";
import { parseJWT } from "@utils/parseJWT";
import { userService } from "@services/user-service";
import InternalStudyStudentsService from "@services/internal-study-students-service";
import { useUnit } from "effector-react";

// AUTH STORE
export const setAuth = createEvent<boolean>();
export const setAuthType = createEvent<"user" | "student" | null>();

export const $Auth = createStore<AuthStorePropsT>({
  isAuth: null,
  type: null,
})
  .on(setAuth, (oldState, newState) => ({
    ...oldState,
    isAuth: newState,
  }))
  .on(setAuthType, (oldState, newState) => ({
    ...oldState,
    type: newState,
  }));

export const useAuth = () => {
  return useUnit($Auth);
};

// REQUESTS
export const checkAuth = createEffect(async () => {
  const accessToken = localStorage.getItem("token");
  if (!accessToken) {
    setAuth(false);
    setAuthType(null);
    return;
  }

  const data = parseJWT(accessToken);
  switch (data.type) {
    case "user":
      setUserLoading(true);
      try {
        const companies = await companyService.getAll();
        let selectedCompany = companies[0];
        const selectedCompanyId = localStorage.getItem("selectedCompany");
        if (selectedCompanyId) {
          try {
            const company = companies.find(
              (company) => company.id === parseInt(selectedCompanyId)
            );
            if (company) {
              selectedCompany = company;
            }
          } catch (err) {
            localStorage.removeItem("selectedCompany");
          }
        }
        setCompany(selectedCompany.id);

        const user = await userService.getById(data.userId);

        let doesTariffExpire = false;
        setUserData({
          ...user,
          doesTariffExpire,
        });
        setUserAddPermissions({
          id: user.id,
          roleId: user.roleId,
          modules: user.additionalPermissions,
          priority: user.role.priority,
          permissions: user.role.permissions.map(
            (permission) => permission.action_name
          ),
        });

        serUserStates({ error: false, isLoading: false, isFetched: true });
        setAuth(true);
        setAuthType(data.type);
      } catch (err) {
        localStorage.removeItem("token");
        localStorage.removeItem("type");
        setAuth(false);
        setAuthType(null);
        setUserLoading(false);
      }
      return;
    case "student":
      setInternalStudentStateLoading(true);
      try {
        const student = await InternalStudyStudentsService.getById(data.userId);
        setInternalStudent(student);

        setInternalStudentStates({
          error: false,
          isLoading: false,
          isFetched: true,
        });
        setAuth(true);
        setAuthType(data.type);
      } catch (err) {
        localStorage.removeItem("token");
        localStorage.removeItem("type");
        setAuth(false);
        setAuthType(null);
        setInternalStudentStateLoading(false);
      }
      return;
  }
});

export const login = createEffect<AuthStoreLoginPropsT, void>(async (data) => {
  const { cb } = data;

  await AuthService.login(
    { email: data.email, password: data.password },
    async (err, res) => {
      if (err || res?.status !== 200) {
        cb(err);
        setUserError(true);
        setInternalStudentStateError(true);
        setAuthType(null);
        return setAuth(false);
      }
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("type", res.data.type);

      const data = parseJWT(res.data.token);
      switch (res.data.type) {
        case "user":
          setUserLoading(true);
          try {
            const companies = await companyService.getAll();
            let selectedCompany = companies[0];
            const selectedCompanyId = localStorage.getItem("selectedCompany");
            if (selectedCompanyId) {
              try {
                const company = companies.find(
                  (company) => company.id === parseInt(selectedCompanyId)
                );
                if (company) {
                  selectedCompany = company;
                }
              } catch (err) {
                localStorage.removeItem("selectedCompany");
              }
            }
            setCompany(selectedCompany.id);

            const user = await userService.getById(data.userId);

            let doesTariffExpire = false;
            setUserData({
              ...user,
              doesTariffExpire,
            });
            setUserAddPermissions({
              id: user.id,
              roleId: user.roleId,
              modules: user.additionalPermissions,
              priority: user.role.priority,
              permissions: user.role.permissions.map(
                (permission) => permission.action_name
              ),
            });

            setAuthType(data.type);
            serUserStates({ error: false, isLoading: false, isFetched: true });
            setAuth(true);
          } catch (err) {
            localStorage.removeItem("token");
            localStorage.removeItem("type");
            setAuth(false);
            setAuthType(null);
            setUserLoading(false);
          }
          break;
        case "student":
          setInternalStudentStateLoading(true);
          try {
            const student = await InternalStudyStudentsService.getById(
              data.userId
            );
            setInternalStudent(student);

            setAuthType(data.type);
            setInternalStudentStates({
              error: false,
              isLoading: false,
              isFetched: true,
            });
            setAuth(true);
          } catch (err) {
            localStorage.removeItem("token");
            localStorage.removeItem("type");
            setAuth(false);
            setAuthType(null);
            setInternalStudentStateLoading(false);
          }
          break;
      }
    }
  );
});

export const signup = createEffect<AuthStoreSignupPropsT, void>(
  async (data) => {
    const { cb } = data;

    await AuthService.signup(
      {
        email: data.email,
        name: data.name,
        password: data.password,
        passwordConfirm: data.passwordConfirm,
      },
      (err, res) => {
        if (err || res?.status !== 200) {
          cb(err);
          setAuthType(null);
          return setAuth(false);
        }
        cb(null);
      }
    );
  }
);
